import React, { useState, useCallback } from 'react'
import { NavigateFn } from '@reach/router'
import { Filters, UseBookingsHook } from './hooks/useBookings'
import {
  FilterButton,
  IconButton,
  prettyNumber,
  Button,
  Icon,
  FilterContainer,
  DateRangeSelect,
  MultiLocationSelect,
  MultiHRLocationSelect,
  MultiDepartmentSelect,
  MultiWPGSelect,
  MultiSpaceSelect,
  MultiEmployeeSelect,
  MultiUsageTypeSelect,
  Toaster,
  Popover,
} from '@ui'
import { pluralize } from '@utils'
import { Classes, Menu, MenuItem, Collapse, H6, Position } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { DateRange } from '@blueprintjs/datetime'
import BookingCancelDialog from './BookingCancelDialog'
import { useFileDownload, useUser } from '@hooks'
import api from '@api'
import { useReload } from '@hooks/reloadContext'
import css from './toolbar.module.scss'

export default function Toolbar({
  applyFilters,
  filters,
  selection,
  status,
  count,
  resetFilters,
  fetchExportLink,
  navigate,
  items,
  usageTypeIds,
  showFilters,
  setShowFilters,
}: UseBookingsHook & {
  navigate?: NavigateFn
  showFilters: boolean
  setShowFilters: (showFilters: boolean) => void
} & {
  usageTypeIds: string[]
}) {
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const startFileDownload = useFileDownload()
  const { user } = useUser()
  const { bookings } = useReload()

  const bookingCheckInCancelEnabled = user?.company.settings.bookingCheckInCancelEnabled
  const bookingCancellationEnabled = user?.company.settings.bookingCancellationEnabled
  const cancelBookingDisabled =
    !bookingCheckInCancelEnabled &&
    items.some(({ data }) => selection?.items[data.id] && data.checkedIn)
  const cancelCheckInMsg =
    'You don’t have permission to cancel this booking. Please contact the administrator to make this change'

  const onDateRangeChange = useCallback(
    (newRange: DateRange | null) => {
      if (newRange) {
        const newFilters = {
          ...filters,
          fromDate: newRange[0],
          toDate: newRange[1],
        }
        applyFilters(newFilters as Filters)
      }
    },
    [applyFilters, filters]
  )

  let filterCount = 0

  if (filters.HRLocations.length) {
    filterCount++
  }

  if (filters.locations.length) {
    filterCount++
  }

  if (filters.departments.length) {
    filterCount++
  }

  if (filters.spaces.length) {
    filterCount++
  }

  if (filters.WPGs.length) {
    filterCount++
  }

  if (filters.employees.length) {
    filterCount++
  }
  const cancelBooking = () => {
    if (bookingCancellationEnabled) {
      setShowCancelDialog(true)
    } else {
      api.booking.cancel
        .bookings({
          bookingIds: Object.keys(selection?.items || {}),
        })
        .then(() => {
          Toaster.success({ message: pluralize(selection?.count || 0, 'Booking') + ' cancelled' })
          bookings()
        })
    }
  }
  return (
    <div
      className={css.bookingManagementToolbar}
      style={{
        height: showFilters ? 210 : 70,
      }}
    >
      <div className="m-4 flex justify-between" style={{ paddingLeft: 24 }}>
        {selection?.count ? (
          <Tooltip2 position="right" content={cancelBookingDisabled ? cancelCheckInMsg : undefined}>
            <Button
              iconName="trash"
              text={pluralize(selection?.count || 0, 'Cancel Booking')}
              intent="danger"
              onClick={() => (cancelBookingDisabled ? null : cancelBooking())}
            />
          </Tooltip2>
        ) : (
          <div className="flex items-baseline">
            <FilterButton filterCount={filterCount} onClick={() => setShowFilters(!showFilters)} />
            {filterCount ? (
              <div className="flex items-baseline">
                <span
                  style={{ marginLeft: 10, marginRight: 4 }}
                  className={status === 'loading' ? Classes.SKELETON : ''}
                >
                  {prettyNumber({ value: count })} filtered {pluralize(count, 'record')}
                </span>
                <IconButton
                  minimal
                  round
                  iconName="times"
                  tooltip="Clear filters"
                  onClick={resetFilters}
                />
              </div>
            ) : (
              <span
                style={{ marginLeft: 10, marginRight: 4 }}
                className={status === 'loading' ? Classes.SKELETON : ''}
              >
                {prettyNumber({ value: count })} {pluralize(count, 'record')}
              </span>
            )}
          </div>
        )}

        <Popover
          minimal
          position={Position.BOTTOM}
          content={
            <Menu>
              <MenuItem
                text="Import"
                onClick={() => {
                  navigate?.('?newItem=bookingsImport')
                }}
                icon={<Icon style={{ marginTop: 2, width: 22 }} name="cloud-upload" />}
              />
              <MenuItem
                text="Import History"
                onClick={() => navigate?.('/bookings/history')}
                icon={<Icon style={{ marginTop: 2, width: 22 }} name="history" />}
              />
              <MenuItem
                text="Download CSV"
                icon={<Icon style={{ marginTop: 2, width: 22 }} name="download" />}
                onClick={() => fetchExportLink().then((url) => startFileDownload(url))}
              />
            </Menu>
          }
        >
          <IconButton round={false} className="ml2" iconName="ellipsis-h" tooltip="More actions" />
        </Popover>
      </div>
      <Collapse isOpen={showFilters} keepChildrenMounted>
        <div className="flex mb-2" style={{ paddingLeft: 24 }}>
          <FilterContainer>
            <H6>Date Range</H6>
            <DateRangeSelect
              past={false}
              future={true}
              value={[filters.fromDate as Date | null, filters.toDate as Date | null]}
              onChange={onDateRangeChange}
            />
          </FilterContainer>
          <FilterContainer>
            <MultiLocationSelect
              mode={MultiLocationSelect.Mode.MULTI_FLOOR}
              values={filters.locations}
              onChange={(newSelection) => {
                applyFilters({
                  ...filters,
                  locations: newSelection,
                })
              }}
            />
          </FilterContainer>
          <FilterContainer>
            <MultiSpaceSelect
              usageTypeIds={usageTypeIds}
              values={filters.spaces}
              onChange={(spaces) => {
                applyFilters({
                  ...filters,
                  spaces,
                })
              }}
            />
          </FilterContainer>
          <FilterContainer>
            <MultiHRLocationSelect
              values={filters.HRLocations}
              onChange={(hrLocations) => {
                applyFilters({
                  ...filters,
                  HRLocations: hrLocations,
                })
              }}
            />
          </FilterContainer>
          <FilterContainer>
            <MultiUsageTypeSelect
              bookableOnly
              values={filters.usageTypes}
              onChange={(usageTypes) => {
                applyFilters({ ...filters, usageTypes })
              }}
            />
          </FilterContainer>
          <FilterContainer>
            <MultiDepartmentSelect
              values={filters.departments}
              onChange={(departments) => {
                applyFilters({
                  ...filters,
                  departments,
                })
              }}
            />
          </FilterContainer>
          <FilterContainer>
            <MultiWPGSelect
              values={filters.WPGs}
              onChange={(WPGs) =>
                applyFilters({
                  ...filters,
                  WPGs,
                })
              }
            />
          </FilterContainer>
          <FilterContainer>
            <MultiEmployeeSelect
              title="Booked For"
              reselectAllItems={false}
              values={filters.employees}
              onChange={(employees) => {
                applyFilters({
                  ...filters,
                  employees,
                })
              }}
            />
          </FilterContainer>
        </div>
      </Collapse>
      {showCancelDialog && (
        <BookingCancelDialog
          cancelIds={Object.keys(selection?.items || [])}
          onClose={() => setShowCancelDialog(false)}
        />
      )}
    </div>
  )
}
